/* eslint-disable no-undef, no-underscore-dangle, camelcase */
// import Vue from 'vue';
import moment from 'moment-timezone';
import {
  getAppointments,
  deleteAppointment,
} from '@/services/appointment';

import { getDayFromCalendarDate } from '@/utils';

import {
  CLEAR_APPOINTMENTS,
  GET_APPOINTMENTS,
  SET_CURRENT_APPOINTMENTS,
  SET_CURRENT_DATES,
  SET_CURRENT_CALENDAR_DAY,
  APPOINTMENT_SELECTED,
  APPOINTMENT_DELETED,
  LOADING,
  LOADING_SUCCESS,
  UNSELECT_APPOINTMENT,
  SET_FETCHED_APPOINTMENT_DATES,
  APPOINTMENT_UPDATED,
  APPOINTMENT_ADDED,
} from './mutation-types';

moment.defaultFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

const mapFloatToColor = ({ color_r, color_g, color_b }) => {
  const R = parseInt(255 * color_r, 10);
  const G = parseInt(255 * color_g, 10);
  const B = parseInt(255 * color_b, 10);
  return `rgb(${R}, ${G}, ${B})`;
};

const mapAppointments = currentTimezone =>
  app => ({
    ...app,
    id: app._id,
    start: moment.tz(app.start_time, currentTimezone),
    end: moment.tz(app.end_time, currentTimezone),
    title: `${app.client.first_name} ${app.client.last_name}`,
    color: mapFloatToColor(app.type_info),
  });

const filterCurrentApps = (apps, start, end) =>
  apps.filter(app => start.isSameOrBefore(app.start) && end.isSameOrAfter(app.end));

const returnFilteredApps = (commit, state, resolve, start, end) => {
  const currentApps = filterCurrentApps(state.appointments, start, end);
  commit(SET_CURRENT_APPOINTMENTS, currentApps);
  return resolve(currentApps);
};

const fetchApps = (dispatch, commit, state, resolve, reject, start, end) =>
  dispatch('getAppointments', { start, end })
    .then(() => returnFilteredApps(commit, state, resolve, start, end))
    .catch(err => reject(err));

const getNewStartEndDates = (start, end, rootGetters) => {
  const tz = rootGetters.currentTimezone;
  const newEnd = moment.tz(end, tz).add(1, 'months').endOf('month');
  const newStart = moment.tz(start, tz).subtract(1, 'months').startOf('month');
  return {
    start: newStart,
    end: newEnd,
  };
};

const updateAppointmentType = (app, appointmentTypes) => {
  app.type_info = appointmentTypes.find(type => type._id === app.type);
  app.color = mapFloatToColor(app.type_info);
  return app;
};

const isAppointmentInCurrentRange = (app, start, end) =>
  (start.isSameOrBefore(app.start) && end.isSameOrAfter(app.end));

const appointment = {
  state: {
    appointments: [],
    currentAppointments: [],
    currentStartDate: null,
    currentEndDate: null,
    currentCalendarDay: null,
    fetchedStartDate: null,
    fetchedEndDate: null,
    selectedAppointment: null,
  },
  mutations: {
    [CLEAR_APPOINTMENTS](state) {
      state.currentAppointments = [];
      state.appointments = [];
      state.currentStartDate = null;
      state.currentEndDate = null;
      state.currentCalendarDay = null;
      state.selectedAppointment = null;
      state.fetchedStartDate = null;
      state.fetchedEndDate = null;
    },
    [GET_APPOINTMENTS](state, appointments) {
      state.appointments = appointments;
    },
    [APPOINTMENT_SELECTED](state, selectedAppointment) {
      state.selectedAppointment = selectedAppointment;
    },
    [UNSELECT_APPOINTMENT](state) {
      state.selectedAppointment = null;
    },
    [APPOINTMENT_DELETED](state, app) {
      state.appointments = state.appointments.filter(a => a._id !== app._id);
      if (isAppointmentInCurrentRange(app, state.currentStartDate, state.currentEndDate)) {
        state.currentAppointments = state.currentAppointments.filter(ca => ca._id !== app._id);
      }
    },
    [SET_CURRENT_APPOINTMENTS](state, currentAppointments) {
      state.currentAppointments = currentAppointments;
    },
    [SET_CURRENT_DATES](state, { start, end }) {
      state.currentStartDate = start;
      state.currentEndDate = end;
    },
    [SET_CURRENT_CALENDAR_DAY](state, day) {
      state.currentCalendarDay = day;
    },
    [SET_FETCHED_APPOINTMENT_DATES](state, { start, end }) {
      state.fetchedStartDate = start;
      state.fetchedEndDate = end;
    },
    [APPOINTMENT_UPDATED](state, { updatedApp, appointmentTypes }) {
      let newApp;
      let oldApp;
      const newAppArray = state.appointments.map((app) => {
        if (app._id !== updatedApp._id) {
          return app;
        }
        oldApp = app;
        newApp = {
          ...app,
          ...updatedApp,
          start: updatedApp.start_time,
          end: updatedApp.end_time,
        };
        if (app.type !== updatedApp.type) {
          return updateAppointmentType(newApp, appointmentTypes);
        }
        return newApp;
      });

      // updated appointment not in list, probably from a notification
      // return as it will be fetched when navigating calendar
      if (!newApp) {
        return;
      }

      state.appointments = newAppArray;

      if (isAppointmentInCurrentRange(oldApp, state.currentStartDate, state.currentEndDate)) {
        state.currentAppointments = filterCurrentApps(
          state.appointments,
          state.currentStartDate,
          state.currentEndDate,
        );
      }
    },
    [APPOINTMENT_ADDED](state, newApp) {
      state.appointments.push(newApp);
      if (isAppointmentInCurrentRange(newApp, state.currentStartDate, state.currentEndDate)) {
        state.currentAppointments = filterCurrentApps(
          state.appointments,
          state.currentStartDate,
          state.currentEndDate,
        );
      }
    },
  },
  actions: {
    clearAppointments({ commit }) {
      commit(CLEAR_APPOINTMENTS);
    },
    getAppointments({ commit, rootGetters, state }, { start, end }) {
      return new Promise((resolve, reject) => {
        commit(LOADING);
        commit(SET_FETCHED_APPOINTMENT_DATES, getNewStartEndDates(start, end, rootGetters));
        return getAppointments(state.fetchedStartDate.format(), state.fetchedEndDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ'))
          .then((res) => {
            const { data } = res;
            if (!data || !data.appointments) {
              const err = new Error('No data in response');
              commit(LOADING_SUCCESS);
              reject(err);
            } else {
              const appointments = data.appointments
                .map(mapAppointments(rootGetters.currentTimezone));
              commit(GET_APPOINTMENTS, appointments);
              commit(LOADING_SUCCESS);
              resolve(appointments);
            }
          })
          .catch((err) => {
            // @todo add loading error action
            commit(LOADING_SUCCESS);
            reject(err);
          });
      });
    },
    getCurrentAppointments({
      commit, dispatch, state, rootGetters,
    }, { startDate, endDate }) {
      const start = moment.tz(getDayFromCalendarDate(startDate), rootGetters.currentTimezone);
      const end = moment.tz(getDayFromCalendarDate(endDate), rootGetters.currentTimezone);
      return new Promise((resolve, reject) => {
        if (state.currentStartDate && state.currentEndDate &&
            state.currentStartDate.isSame(start) &&
            state.currentEndDate.isSame(end)) {
          return resolve(state.currentAppointments);
        }
        commit(SET_CURRENT_DATES, { start, end });

        if (!state.fetchedStartDate || !state.fetchedEndDate ||
          (start.isBefore(state.fetchedStartDate) ||
          end.isAfter(state.fetchedEndDate))) {
          return fetchApps(dispatch, commit, state, resolve, reject, start, end);
        }

        return returnFilteredApps(commit, state, resolve, start, end);
      });
    },
    setCurrentCalendarDay({ commit }, day) {
      commit(SET_CURRENT_CALENDAR_DAY, day);
    },
    selectAppointment({ commit }, app) {
      return new Promise((resolve, reject) => {
        if (!app || !app.client_id) {
          const err = new Error('No appointment selected');
          reject(err);
        }
        commit(APPOINTMENT_SELECTED, app);
        resolve(app);
      });
    },
    deleteAppointment({ commit }, app) {
      return new Promise((resolve, reject) => {
        if (!app) {
          const err = new Error('Missing appointment');
          reject(err);
        }
        commit(LOADING);
        return deleteAppointment(app)
          .then(() => {
            commit(LOADING_SUCCESS);
            commit(APPOINTMENT_DELETED, app);
            resolve(app);
          })
          .catch((err) => {
            commit(LOADING_SUCCESS);
            reject(err);
          });
      });
    },
    changeAppointment({ commit, rootState }, updatedApp) {
      commit(APPOINTMENT_UPDATED, {
        updatedApp,
        appointmentTypes: rootState.appointmentTypes.list,
      });
    },
    addAppointment({ commit, dispatch, rootGetters }, newApp) {
      return new Promise((resolve, reject) =>
        dispatch('getClientById', newApp.client_id)
          .then((client) => {
            newApp.client = client;
            const [mappedApp] = [newApp].map(mapAppointments(rootGetters.currentTimezone));
            commit(APPOINTMENT_ADDED, mappedApp);
            resolve(mappedApp);
          })
          .catch(err => reject(err)));
    },
  },
};

export default appointment;



// WEBPACK FOOTER //
// ./src/store/appointment.js